import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, Send, ShieldAlert } from "lucide-react";
import PublicShell from "@/components/PublicShell";
import { api, errMsg } from "@/lib/api";

export default function Reissue() {
  const { token = "" } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<any>(null);
  useEffect(() => { api.get(`/public/reissue/${token}`).then((r) => setInfo(r.data)).catch((e) => setErr(errMsg(e))); }, [token]);
  const send = async () => {
    setBusy(true); setErr("");
    try { setDone((await api.post(`/public/reissue/${token}`)).data); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const blocked = info && (info.reissued_at || info.deactivated || info.expired || !info.unused_available || info.original_works);
  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-4 py-16">
        <div className="card-soft fade-up p-8 sm:p-10" data-testid="reissue-card">
          <h1 className="text-3xl font-extrabold tracking-tight">Send a new screening code</h1>
          {!info && !err && <Loader2 className="mt-6 h-6 w-6 animate-spin text-[#94A3B8]" />}
          {info && (
            <div className="mt-5 space-y-1 text-[15px] text-[#475569]" data-testid="reissue-info">
              <p>Applicant: <b className="text-[#1A2536]">{info.name}</b></p>
              <p>Organization: <b className="text-[#1A2536]">{info.org_name}</b></p>
              <p>Blocked code: <span className="font-mono">{info.old_code}</span></p>
              <p>Unused codes available: <b className="text-[#1A2536]" data-testid="reissue-available">{info.unused_available}</b></p>
            </div>
          )}
          {done ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#DCFCE7] px-4 py-3 text-[15px] text-[#166534]" data-testid="reissue-done">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <span>Code <b className="font-mono">{done.code}</b> was emailed to {done.name}. It's now set aside for them. We also emailed your organization's contacts which code was sent; please mark it as given out in your records.</span>
            </div>
          ) : info && (
            <>
              {info.reissued_at && <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-sm" data-testid="reissue-already">A new code was already sent to this applicant on {String(info.reissued_at).slice(0, 10)}.</p>}
              {!info.reissued_at && info.deactivated && <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-sm" data-testid="reissue-deactivated">This link is no longer active. Applicants can now continue with their original code, so no new code is needed and none of your codes will be used.</p>}
              {!info.reissued_at && !info.deactivated && info.expired && <p className="mt-6 rounded-xl bg-[#F1F5F9] px-4 py-3 text-sm" data-testid="reissue-expired">This link has expired. Please send the applicant a code yourself.</p>}
              {!info.reissued_at && info.original_works && <p className="mt-6 rounded-xl bg-[#DCFCE7] px-4 py-3 text-sm text-[#166534]" data-testid="reissue-original-works">No new code is needed. {info.name}'s original code <b className="font-mono">{info.old_code}</b> works again; they can simply enter it again to continue. None of your codes will be used.</p>}
              {!info.reissued_at && !info.original_works && !info.expired && !info.unused_available && <p className="mt-6 rounded-xl bg-[#FEF3C7] px-4 py-3 text-sm text-[#92400E]" data-testid="reissue-none">Your organization has no unused codes left. <a href="/order" className="font-semibold underline">Order more codes</a>, then come back to this link.</p>}
              <button className="btn-primary mt-6 h-14 w-full bg-[#F26B4F] text-lg hover:bg-[#E0553A]" disabled={busy || !!blocked} onClick={send} data-testid="reissue-send-button">
                {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="h-5 w-5" /> Email {info.name} a new code</>}
              </button>
            </>
          )}
          {err && <div className="mt-4 flex items-start gap-3 rounded-xl bg-[#FEE2E2] px-4 py-3 text-[15px] text-[#991B1B]" data-testid="reissue-error"><ShieldAlert className="mt-0.5 h-5 w-5 shrink-0" /> {err}</div>}
        </div>
      </div>
    </PublicShell>
  );
}
